(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nf-interactive-home]';

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function ease(value) {
    return value < 0.5
      ? 4 * value * value * value
      : 1 - Math.pow(-2 * value + 2, 3) / 2;
  }

  function initInteractiveHome(root) {
    if (!root || root.nfInteractiveHomeCleanup) return;

    var pin = root.querySelector('[data-nf-ih-pin]');
    var viewport = root.querySelector('[data-nf-ih-viewport]');
    var house = root.querySelector('[data-nf-ih-house]');
    var rooms = Array.prototype.slice.call(root.querySelectorAll('[data-nf-ih-room]'));
    var navItems = Array.prototype.slice.call(root.querySelectorAll('[data-nf-ih-nav]'));
    var status = root.querySelector('[data-nf-ih-status]');
    var reducedButtons = Array.prototype.slice.call(root.querySelectorAll('[data-nf-ih-play]'));
    var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var listenerOptions = controller ? { passive: true, signal: controller.signal } : { passive: true };
    var clickOptions = controller ? { signal: controller.signal } : false;
    var observer = null;
    var activeIndex = 0;
    var targetY = 0;
    var currentY = 0;
    var targetDepth = 0;
    var currentDepth = 0;
    var maxTranslate = 1;
    var sectionTop = 0;
    var scrollable = 1;
    var frame = 0;
    var hasUserScrolled = false;
    var lastScrollY = window.scrollY || window.pageYOffset || 0;

    if (!pin || !viewport || !house || !rooms.length) return;

    function isReduced() {
      return Boolean(motionQuery && motionQuery.matches);
    }

    function injectVideo(video) {
      var src = video && video.getAttribute('data-clip');
      if (!src || video.dataset.loaded === 'true') return false;
      video.src = src;
      video.dataset.loaded = 'true';
      video.load();
      return true;
    }

    function pauseVideo(video, reset) {
      if (!video) return;
      video.pause();
      if (reset) {
        try {
          video.currentTime = 0;
        } catch (error) {
          // Metadata may not exist yet because clips are lazy-injected.
        }
      }
    }

    function playRoomClip(room) {
      var video = room.querySelector('[data-nf-ih-video]');
      if (!video || !video.getAttribute('data-clip') || isReduced() || !hasUserScrolled) return;
      injectVideo(video);
      room.classList.add('has-active-clip');
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          room.classList.remove('has-active-clip');
        });
      }
    }

    function stopInactiveClips(nextIndex) {
      rooms.forEach(function (room, index) {
        if (index === nextIndex) return;
        room.classList.remove('has-active-clip');
        pauseVideo(room.querySelector('[data-nf-ih-video]'), true);
      });
    }

    function setActive(index) {
      var nextIndex = clamp(index, 0, rooms.length - 1);
      var label = rooms[nextIndex].getAttribute('data-room-label') || '';

      rooms.forEach(function (room, roomIndex) {
        room.classList.toggle('is-active', roomIndex === nextIndex);
      });

      navItems.forEach(function (item, itemIndex) {
        item.classList.toggle('is-active', itemIndex === nextIndex);
      });

      if (status) status.textContent = label;

      if (nextIndex !== activeIndex) {
        stopInactiveClips(nextIndex);
      }

      activeIndex = nextIndex;
      playRoomClip(rooms[nextIndex]);
    }

    function measure() {
      var rect = pin.getBoundingClientRect();
      sectionTop = rect.top + (window.scrollY || window.pageYOffset || 0);
      scrollable = Math.max(1, pin.offsetHeight - window.innerHeight);
      maxTranslate = Math.max(1, house.scrollHeight - viewport.offsetHeight);
    }

    function updateTargets() {
      var scrollY = window.scrollY || window.pageYOffset || 0;
      var raw = clamp((scrollY - sectionTop) / scrollable, 0, 1);
      var stepped = raw * (rooms.length - 1);
      var baseIndex = Math.floor(stepped);
      var local = stepped - baseIndex;
      var easedStep = baseIndex + ease(local);
      var easedProgress = clamp(easedStep / Math.max(1, rooms.length - 1), 0, 1);
      var nextIndex = clamp(Math.round(stepped), 0, rooms.length - 1);

      if (Math.abs(scrollY - lastScrollY) > 2 && raw > 0.004) {
        hasUserScrolled = true;
      }
      lastScrollY = scrollY;

      targetY = -maxTranslate * easedProgress;
      targetDepth = raw;
      setActive(nextIndex);
      requestFrame();
    }

    function render() {
      var diffY = targetY - currentY;
      var diffDepth = targetDepth - currentDepth;
      currentY += diffY * 0.18;
      currentDepth += diffDepth * 0.22;

      if (Math.abs(diffY) < 0.2) currentY = targetY;
      if (Math.abs(diffDepth) < 0.001) currentDepth = targetDepth;

      root.style.setProperty('--nf-ih-y', currentY.toFixed(2) + 'px');
      root.style.setProperty('--nf-ih-depth', currentDepth.toFixed(4));

      if (currentY !== targetY || currentDepth !== targetDepth) {
        frame = window.requestAnimationFrame(render);
      } else {
        frame = 0;
      }
    }

    function requestFrame() {
      if (!frame) frame = window.requestAnimationFrame(render);
    }

    function handleIntersection(entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || !hasUserScrolled) return;
        if (!entry.target.classList.contains('is-active')) return;
        playRoomClip(entry.target);
      });
    }

    function createObserver() {
      if (!('IntersectionObserver' in window) || isReduced()) return;
      observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.01,
        rootMargin: '-40% 0px -40% 0px'
      });
      rooms.forEach(function (room) { observer.observe(room); });
    }

    function handleResize() {
      measure();
      updateTargets();
    }

    function handleMotionChange() {
      if (isReduced()) {
        stopInactiveClips(-1);
      }
      measure();
      updateTargets();
    }

    function playReducedClip(button) {
      var src = button.getAttribute('data-clip');
      if (!src) return;
      var article = button.closest('article');
      var existing = article ? article.querySelector('video') : null;
      var video = existing || document.createElement('video');

      if (!existing && article) {
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';
        video.muted = true;
        video.src = src;
        article.insertBefore(video, article.firstChild);
      }

      if (existing && !existing.src) existing.src = src;
      video.play();
    }

    reducedButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        playReducedClip(button);
      }, clickOptions);
    });

    root.classList.add('is-ready');
    measure();
    setActive(0);
    updateTargets();
    createObserver();

    window.addEventListener('scroll', updateTargets, listenerOptions);
    window.addEventListener('resize', handleResize, listenerOptions);

    if (motionQuery) {
      if (typeof motionQuery.addEventListener === 'function') {
        motionQuery.addEventListener('change', handleMotionChange, controller ? { signal: controller.signal } : false);
      } else if (typeof motionQuery.addListener === 'function') {
        motionQuery.addListener(handleMotionChange);
      }
    }

    root.nfInteractiveHomeCleanup = function () {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      stopInactiveClips(-1);
      if (controller) controller.abort();
      if (motionQuery && typeof motionQuery.removeListener === 'function') {
        motionQuery.removeListener(handleMotionChange);
      }
      delete root.nfInteractiveHomeCleanup;
    };
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches(ROOT_SELECTOR)) initInteractiveHome(context);
    Array.prototype.forEach.call(context.querySelectorAll(ROOT_SELECTOR), initInteractiveHome);
  }

  function unloadAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && scope.matches(ROOT_SELECTOR) && scope.nfInteractiveHomeCleanup) {
      scope.nfInteractiveHomeCleanup();
    }
    Array.prototype.forEach.call(scope.querySelectorAll(ROOT_SELECTOR), function (root) {
      if (root.nfInteractiveHomeCleanup) root.nfInteractiveHomeCleanup();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    unloadAll(event.target);
  });
})();
